import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useData } from "../contexts/DataContext";

const STEPS = [
  { id: 1, title: "Company Info", description: "Basic details" },
  { id: 2, title: "Add School", description: "School profile" },
  { id: 3, title: "Add Students", description: "Pupil records" },
  { id: 4, title: "Add Parents", description: "Guardian accounts" },
  { id: 5, title: "Add Driver", description: "Driver account" },
  { id: 6, title: "Assign Vehicle", description: "Vehicle assignment" },
  { id: 7, title: "Create First Trip", description: "Schedule trip" },
];

export default function SetupWizard() {
  const navigate = useNavigate();
  const { saveSettings, createSchool, createChild, createUser, updateChild, createVehicle, createTrip } = useData();

  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [completed, setCompleted] = useState(false);

  // Form state for all steps
  const [companyInfo, setCompanyInfo] = useState({
    company_name: "",
    company_email: "",
    contact_phone: "",
    address: "",
    support_url: "",
  });

  const [school, setSchool] = useState({
    name: "",
    address: "",
    contact_name: "",
    contact_phone: "",
    contact_email: "",
  });

  const [students, setStudents] = useState([
    {
      name: "",
      pickup_location: "",
      dropoff_location: "",
      notes: "",
    },
  ]);

  const [parents, setParents] = useState([
    {
      name: "",
      email: "",
      phone: "",
      address: "",
      password: "",
      studentIndex: 0,
    },
  ]);

  const [driver, setDriver] = useState({
    name: "",
    email: "",
    password: "",
    license_number: "",
  });

  const [vehicle, setVehicle] = useState({
    registration: "",
    type: "",
    capacity: 4,
  });

  const [trip, setTrip] = useState({
    studentIndex: 0,
    scheduled_start_time: "",
    notes: "",
  });

  // IDs created during wizard flow
  const [createdIds, setCreatedIds] = useState({
    schoolId: null,
    studentIds: [],
    parentIds: [],
    driverId: null,
    vehicleId: null,
    tripId: null,
  });

  const addStudent = () => {
    setStudents([
      ...students,
      {
        name: "",
        pickup_location: "",
        dropoff_location: "",
        notes: "",
      },
    ]);
  };

  const removeStudent = (index) => {
    setStudents(students.filter((_, i) => i !== index));
  };

  const addParent = () => {
    setParents([
      ...parents,
      {
        name: "",
        email: "",
        phone: "",
        address: "",
        password: "",
        studentIndex: 0,
      },
    ]);
  };

  const removeParent = (index) => {
    setParents(parents.filter((_, i) => i !== index));
  };

  const validateStep = () => {
    if (currentStep === 1) {
      if (!companyInfo.company_name || !companyInfo.company_email || !companyInfo.contact_phone) {
        toast.error("Please complete all required company information fields.");
        return false;
      }
    }
    if (currentStep === 2) {
      if (!school.name || !school.address || !school.contact_name || !school.contact_phone || !school.contact_email) {
        toast.error("Please complete all school information fields.");
        return false;
      }
    }
    if (currentStep === 3) {
      if (students.length === 0 || students.some((s) => !s.name || !s.pickup_location || !s.dropoff_location)) {
        toast.error("Please complete all student fields. At least one student is required.");
        return false;
      }
    }
    if (currentStep === 4) {
      if (parents.length === 0 || parents.some((p) => !p.name || !p.email || !p.phone || !p.address || !p.password)) {
        toast.error("Please complete all parent/guardian fields. At least one parent is required.");
        return false;
      }
    }
    if (currentStep === 5) {
      if (!driver.name || !driver.email || !driver.password || !driver.license_number) {
        toast.error("Please complete all driver fields.");
        return false;
      }
    }
    if (currentStep === 6) {
      if (!vehicle.registration || !vehicle.type || vehicle.capacity < 1) {
        toast.error("Please complete all vehicle fields.");
        return false;
      }
    }
    if (currentStep === 7) {
      if (!trip.scheduled_start_time) {
        toast.error("Please select a scheduled start time for the trip.");
        return false;
      }
    }
    return true;
  };

  const handleNext = async () => {
    if (!validateStep()) return;

    if (currentStep === 7) {
      await handleComplete();
      return;
    }

    setCurrentStep(currentStep + 1);
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = async () => {
    setLoading(true);
    try {
      // Step 1: Save company settings
      const settingsResult = await saveSettings(companyInfo);
      if (!settingsResult.success) {
        toast.error(`Failed to save company settings: ${settingsResult.error}`);
        setLoading(false);
        return;
      }

      // Step 2: Create school
      const schoolResult = await createSchool(school);
      if (!schoolResult.success) {
        toast.error(`Failed to create school: ${schoolResult.error}`);
        setLoading(false);
        return;
      }
      const schoolId = schoolResult.data.id;

      // Step 3: Create students
      const studentIds = [];
      for (const student of students) {
        const studentResult = await createChild({
          ...student,
          school_id: schoolId,
        });
        if (!studentResult.success) {
          toast.error(`Failed to create student: ${studentResult.error}`);
          setLoading(false);
          return;
        }
        studentIds.push(studentResult.data.id);
      }

      // Step 4: Create parents
      const parentIds = [];
      for (const parent of parents) {
        // Create user account for parent
        const parentUserResult = await createUser({
          name: parent.name,
          email: parent.email,
          password: parent.password,
          role: "parent",
          parent_phone: parent.phone,
          address: parent.address,
        });
        if (!parentUserResult.success) {
          toast.error(`Failed to create parent account: ${parentUserResult.error}`);
          setLoading(false);
          return;
        }
        parentIds.push(parentUserResult.data.id);

        // The backend auto-creates a parent profile when creating a parent user
        // We need to fetch the parent profiles and link children
        // For now, we'll update via the child record instead
        const studentId = studentIds[parent.studentIndex] || studentIds[0];
        const updateChildResult = await updateChild(studentId, {
          parent_id: parentUserResult.data.id,
        });
        if (!updateChildResult.success) {
          toast.warning(`Parent created but linking to student may need manual adjustment.`);
        }
      }

      // Step 5: Create driver
      const driverResult = await createUser({
        name: driver.name,
        email: driver.email,
        password: driver.password,
        role: "driver",
        license_number: driver.license_number,
      });
      if (!driverResult.success) {
        toast.error(`Failed to create driver account: ${driverResult.error}`);
        setLoading(false);
        return;
      }
      const driverId = driverResult.data.id;

      // Step 6: Create vehicle and assign driver
      const vehicleResult = await createVehicle({
        ...vehicle,
        assigned_driver_id: driverId,
      });
      if (!vehicleResult.success) {
        toast.error(`Failed to create vehicle: ${vehicleResult.error}`);
        setLoading(false);
        return;
      }
      const vehicleId = vehicleResult.data.id;

      // Step 7: Create first trip
      const selectedStudentId = studentIds[trip.studentIndex] || studentIds[0];
      const tripResult = await createTrip({
        child_id: selectedStudentId,
        driver_id: driverId,
        vehicle_id: vehicleId,
        scheduled_start_time: trip.scheduled_start_time,
        notes: trip.notes,
      });
      if (!tripResult.success) {
        toast.error(`Failed to create trip: ${tripResult.error}`);
        setLoading(false);
        return;
      }

      // Store created IDs for summary
      setCreatedIds({
        schoolId,
        studentIds,
        parentIds,
        driverId,
        vehicleId,
        tripId: tripResult.data.id,
      });

      setCompleted(true);
      toast.success("Client onboarding completed successfully!");
    } catch (error) {
      toast.error(`Unexpected error during setup: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  if (completed) {
    return (
      <div className="min-h-screen bg-[#F5F7FA] px-6 py-8">
        <div className="mx-auto max-w-3xl">
          <div className="guardian-card bg-white p-8 text-center">
            <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-emerald-50">
              <CheckCircle2 className="text-emerald-600" size={48} />
            </div>
            <h1 className="mt-6 font-['Manrope'] text-3xl font-semibold text-[#0F172A]">Client Onboarding Complete!</h1>
            <p className="mt-3 text-[#64748B]">
              You've successfully set up a new client with school, students, parents, driver, vehicle, and the first trip.
            </p>
            <div className="mt-8 space-y-2 rounded-2xl bg-[#F8FAFC] p-6 text-left text-sm">
              <p className="font-semibold text-[#0F172A]">What was created:</p>
              <ul className="mt-3 space-y-1 text-[#475569]">
                <li>• Company settings updated</li>
                <li>• 1 School profile</li>
                <li>• {createdIds.studentIds.length} Student record(s)</li>
                <li>• {createdIds.parentIds.length} Parent account(s)</li>
                <li>• 1 Driver account</li>
                <li>• 1 Vehicle (assigned to driver)</li>
                <li>• 1 Trip scheduled</li>
              </ul>
            </div>
            <div className="mt-8 flex items-center justify-center gap-4">
              <button
                onClick={() => navigate("/admin")}
                className="rounded-xl bg-[#163D6B] px-6 py-3 text-sm font-semibold text-white hover:bg-[#0F2A4D]"
              >
                Go to Dashboard
              </button>
              <button
                onClick={() => navigate("/admin/trips")}
                className="rounded-xl border border-[#E2E8F0] px-6 py-3 text-sm font-semibold text-[#0F172A] hover:bg-[#F8FAFC]"
              >
                View Trips
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F7FA] px-6 py-8">
      <div className="mx-auto max-w-5xl">
        {/* Progress indicator */}
        <div className="mb-8 overflow-hidden rounded-[32px] border border-[#E2E8F0] bg-white p-6 shadow-sm">
          <h1 className="font-['Manrope'] text-2xl font-semibold text-[#0F172A]">Client Setup Wizard</h1>
          <p className="mt-1 text-sm text-[#64748B]">Complete all steps to onboard a new client end-to-end</p>
          <div className="mt-6 flex items-center justify-between gap-2">
            {STEPS.map((step, index) => (
              <div key={step.id} className="flex flex-1 flex-col items-center gap-2">
                <div
                  className={`flex h-10 w-10 items-center justify-center rounded-full text-sm font-semibold ${
                    currentStep > step.id
                      ? "bg-emerald-100 text-emerald-700"
                      : currentStep === step.id
                      ? "bg-[#163D6B] text-white"
                      : "bg-[#F8FAFC] text-[#94A3B8]"
                  }`}
                >
                  {currentStep > step.id ? <Check size={18} /> : step.id}
                </div>
                <div className="text-center">
                  <p className={`text-xs font-semibold ${currentStep >= step.id ? "text-[#0F172A]" : "text-[#94A3B8]"}`}>{step.title}</p>
                  <p className="text-xs text-[#94A3B8]">{step.description}</p>
                </div>
                {index < STEPS.length - 1 && (
                  <div className={`h-0.5 w-full ${currentStep > step.id ? "bg-emerald-200" : "bg-[#E2E8F0]"}`} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Step content */}
        <div className="guardian-card bg-white p-8">
          {currentStep === 1 && (
            <div className="space-y-6">
              <div>
                <h2 className="font-['Manrope'] text-xl font-semibold text-[#0F172A]">Company Information</h2>
                <p className="mt-1 text-sm text-[#64748B]">Enter the client company details</p>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-[#0F172A]">Company Name *</label>
                  <input
                    value={companyInfo.company_name}
                    onChange={(e) => setCompanyInfo({ ...companyInfo, company_name: e.target.value })}
                    className="mt-2 w-full rounded-xl border border-[#E2E8F0] px-4 py-3 text-sm"
                    placeholder="e.g., ABC Transport Services"
                  />
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className="text-sm font-medium text-[#0F172A]">Company Email *</label>
                    <input
                      type="email"
                      value={companyInfo.company_email}
                      onChange={(e) => setCompanyInfo({ ...companyInfo, company_email: e.target.value })}
                      className="mt-2 w-full rounded-xl border border-[#E2E8F0] px-4 py-3 text-sm"
                      placeholder="info@company.com"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-[#0F172A]">Contact Phone *</label>
                    <input
                      type="tel"
                      value={companyInfo.contact_phone}
                      onChange={(e) => setCompanyInfo({ ...companyInfo, contact_phone: e.target.value })}
                      className="mt-2 w-full rounded-xl border border-[#E2E8F0] px-4 py-3 text-sm"
                      placeholder="+44 20 1234 5678"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-[#0F172A]">Address</label>
                  <input
                    value={companyInfo.address}
                    onChange={(e) => setCompanyInfo({ ...companyInfo, address: e.target.value })}
                    className="mt-2 w-full rounded-xl border border-[#E2E8F0] px-4 py-3 text-sm"
                    placeholder="123 Fleet Street, London"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#0F172A]">Support URL</label>
                  <input
                    type="url"
                    value={companyInfo.support_url}
                    onChange={(e) => setCompanyInfo({ ...companyInfo, support_url: e.target.value })}
                    className="mt-2 w-full rounded-xl border border-[#E2E8F0] px-4 py-3 text-sm"
                    placeholder="https://company.com"
                  />
                </div>
              </div>
            </div>
          )}

          {currentStep === 2 && (
            <div className="space-y-6">
              <div>
                <h2 className="font-['Manrope'] text-xl font-semibold text-[#0F172A]">School Profile</h2>
                <p className="mt-1 text-sm text-[#64748B]">Add the school that will use this service</p>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-[#0F172A]">School Name *</label>
                  <input
                    value={school.name}
                    onChange={(e) => setSchool({ ...school, name: e.target.value })}
                    className="mt-2 w-full rounded-xl border border-[#E2E8F0] px-4 py-3 text-sm"
                    placeholder="e.g., Oakwood Primary School"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#0F172A]">School Address *</label>
                  <input
                    value={school.address}
                    onChange={(e) => setSchool({ ...school, address: e.target.value })}
                    className="mt-2 w-full rounded-xl border border-[#E2E8F0] px-4 py-3 text-sm"
                    placeholder="123 School Lane, City"
                  />
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className="text-sm font-medium text-[#0F172A]">Contact Name *</label>
                    <input
                      value={school.contact_name}
                      onChange={(e) => setSchool({ ...school, contact_name: e.target.value })}
                      className="mt-2 w-full rounded-xl border border-[#E2E8F0] px-4 py-3 text-sm"
                      placeholder="Jane Smith"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-[#0F172A]">Contact Phone *</label>
                    <input
                      type="tel"
                      value={school.contact_phone}
                      onChange={(e) => setSchool({ ...school, contact_phone: e.target.value })}
                      className="mt-2 w-full rounded-xl border border-[#E2E8F0] px-4 py-3 text-sm"
                      placeholder="+44 20 1234 5678"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-[#0F172A]">Contact Email *</label>
                  <input
                    type="email"
                    value={school.contact_email}
                    onChange={(e) => setSchool({ ...school, contact_email: e.target.value })}
                    className="mt-2 w-full rounded-xl border border-[#E2E8F0] px-4 py-3 text-sm"
                    placeholder="admin@school.edu"
                  />
                </div>
              </div>
            </div>
          )}

          {currentStep === 3 && (
            <div className="space-y-6">
              <div>
                <h2 className="font-['Manrope'] text-xl font-semibold text-[#0F172A]">Add Students</h2>
                <p className="mt-1 text-sm text-[#64748B]">Add one or more students who will use the transport service</p>
              </div>
              {students.map((student, index) => (
                <div key={index} className="space-y-4 rounded-2xl border border-[#E2E8F0] bg-[#F8FAFC] p-6">
                  <div className="flex items-center justify-between">
                    <p className="font-semibold text-[#0F172A]">Student {index + 1}</p>
                    {students.length > 1 && (
                      <button
                        onClick={() => removeStudent(index)}
                        className="text-sm text-rose-600 hover:text-rose-700"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  <div>
                    <label className="text-sm font-medium text-[#0F172A]">Student Name *</label>
                    <input
                      value={student.name}
                      onChange={(e) => {
                        const updated = [...students];
                        updated[index].name = e.target.value;
                        setStudents(updated);
                      }}
                      className="mt-2 w-full rounded-xl border border-[#E2E8F0] bg-white px-4 py-3 text-sm"
                      placeholder="e.g., Sarah Johnson"
                    />
                  </div>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <label className="text-sm font-medium text-[#0F172A]">Pickup Location *</label>
                      <input
                        value={student.pickup_location}
                        onChange={(e) => {
                          const updated = [...students];
                          updated[index].pickup_location = e.target.value;
                          setStudents(updated);
                        }}
                        className="mt-2 w-full rounded-xl border border-[#E2E8F0] bg-white px-4 py-3 text-sm"
                        placeholder="e.g., 45 Oak Street"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-[#0F172A]">Dropoff Location *</label>
                      <input
                        value={student.dropoff_location}
                        onChange={(e) => {
                          const updated = [...students];
                          updated[index].dropoff_location = e.target.value;
                          setStudents(updated);
                        }}
                        className="mt-2 w-full rounded-xl border border-[#E2E8F0] bg-white px-4 py-3 text-sm"
                        placeholder="Usually the school"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-[#0F172A]">Notes</label>
                    <input
                      value={student.notes}
                      onChange={(e) => {
                        const updated = [...students];
                        updated[index].notes = e.target.value;
                        setStudents(updated);
                      }}
                      className="mt-2 w-full rounded-xl border border-[#E2E8F0] bg-white px-4 py-3 text-sm"
                      placeholder="Any special requirements"
                    />
                  </div>
                </div>
              ))}
              <button
                onClick={addStudent}
                className="rounded-xl border-2 border-dashed border-[#CBD5E1] px-4 py-3 text-sm font-semibold text-[#475569] hover:border-[#94A3B8] hover:bg-[#F8FAFC]"
              >
                + Add Another Student
              </button>
            </div>
          )}

          {currentStep === 4 && (
            <div className="space-y-6">
              <div>
                <h2 className="font-['Manrope'] text-xl font-semibold text-[#0F172A]">Add Parents/Guardians</h2>
                <p className="mt-1 text-sm text-[#64748B]">Create parent accounts and link them to students</p>
              </div>
              {parents.map((parent, index) => (
                <div key={index} className="space-y-4 rounded-2xl border border-[#E2E8F0] bg-[#F8FAFC] p-6">
                  <div className="flex items-center justify-between">
                    <p className="font-semibold text-[#0F172A]">Parent/Guardian {index + 1}</p>
                    {parents.length > 1 && (
                      <button
                        onClick={() => removeParent(index)}
                        className="text-sm text-rose-600 hover:text-rose-700"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <label className="text-sm font-medium text-[#0F172A]">Parent Name *</label>
                      <input
                        value={parent.name}
                        onChange={(e) => {
                          const updated = [...parents];
                          updated[index].name = e.target.value;
                          setParents(updated);
                        }}
                        className="mt-2 w-full rounded-xl border border-[#E2E8F0] bg-white px-4 py-3 text-sm"
                        placeholder="John Doe"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-[#0F172A]">Email *</label>
                      <input
                        type="email"
                        value={parent.email}
                        onChange={(e) => {
                          const updated = [...parents];
                          updated[index].email = e.target.value;
                          setParents(updated);
                        }}
                        className="mt-2 w-full rounded-xl border border-[#E2E8F0] bg-white px-4 py-3 text-sm"
                        placeholder="parent@email.com"
                      />
                    </div>
                  </div>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <label className="text-sm font-medium text-[#0F172A]">Phone *</label>
                      <input
                        type="tel"
                        value={parent.phone}
                        onChange={(e) => {
                          const updated = [...parents];
                          updated[index].phone = e.target.value;
                          setParents(updated);
                        }}
                        className="mt-2 w-full rounded-xl border border-[#E2E8F0] bg-white px-4 py-3 text-sm"
                        placeholder="+44 7700 900000"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-[#0F172A]">Password *</label>
                      <input
                        type="password"
                        value={parent.password}
                        onChange={(e) => {
                          const updated = [...parents];
                          updated[index].password = e.target.value;
                          setParents(updated);
                        }}
                        className="mt-2 w-full rounded-xl border border-[#E2E8F0] bg-white px-4 py-3 text-sm"
                        placeholder="••••••••"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-[#0F172A]">Address *</label>
                    <input
                      value={parent.address}
                      onChange={(e) => {
                        const updated = [...parents];
                        updated[index].address = e.target.value;
                        setParents(updated);
                      }}
                      className="mt-2 w-full rounded-xl border border-[#E2E8F0] bg-white px-4 py-3 text-sm"
                      placeholder="123 Home Street"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-[#0F172A]">Link to Student</label>
                    <select
                      value={parent.studentIndex}
                      onChange={(e) => {
                        const updated = [...parents];
                        updated[index].studentIndex = parseInt(e.target.value);
                        setParents(updated);
                      }}
                      className="mt-2 w-full rounded-xl border border-[#E2E8F0] bg-white px-4 py-3 text-sm"
                    >
                      {students.map((student, studentIndex) => (
                        <option key={studentIndex} value={studentIndex}>
                          {student.name || `Student ${studentIndex + 1}`}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              ))}
              <button
                onClick={addParent}
                className="rounded-xl border-2 border-dashed border-[#CBD5E1] px-4 py-3 text-sm font-semibold text-[#475569] hover:border-[#94A3B8] hover:bg-[#F8FAFC]"
              >
                + Add Another Parent
              </button>
            </div>
          )}

          {currentStep === 5 && (
            <div className="space-y-6">
              <div>
                <h2 className="font-['Manrope'] text-xl font-semibold text-[#0F172A]">Add Driver</h2>
                <p className="mt-1 text-sm text-[#64748B]">Create a driver account for this client</p>
              </div>
              <div className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className="text-sm font-medium text-[#0F172A]">Driver Name *</label>
                    <input
                      value={driver.name}
                      onChange={(e) => setDriver({ ...driver, name: e.target.value })}
                      className="mt-2 w-full rounded-xl border border-[#E2E8F0] px-4 py-3 text-sm"
                      placeholder="Michael Brown"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-[#0F172A]">Email *</label>
                    <input
                      type="email"
                      value={driver.email}
                      onChange={(e) => setDriver({ ...driver, email: e.target.value })}
                      className="mt-2 w-full rounded-xl border border-[#E2E8F0] px-4 py-3 text-sm"
                      placeholder="driver@email.com"
                    />
                  </div>
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className="text-sm font-medium text-[#0F172A]">Password *</label>
                    <input
                      type="password"
                      value={driver.password}
                      onChange={(e) => setDriver({ ...driver, password: e.target.value })}
                      className="mt-2 w-full rounded-xl border border-[#E2E8F0] px-4 py-3 text-sm"
                      placeholder="••••••••"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-[#0F172A]">License Number *</label>
                    <input
                      value={driver.license_number}
                      onChange={(e) => setDriver({ ...driver, license_number: e.target.value })}
                      className="mt-2 w-full rounded-xl border border-[#E2E8F0] px-4 py-3 text-sm"
                      placeholder="e.g., DRV123456"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {currentStep === 6 && (
            <div className="space-y-6">
              <div>
                <h2 className="font-['Manrope'] text-xl font-semibold text-[#0F172A]">Assign Vehicle</h2>
                <p className="mt-1 text-sm text-[#64748B]">Add a vehicle and assign it to the driver</p>
              </div>
              <div className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className="text-sm font-medium text-[#0F172A]">Registration Number *</label>
                    <input
                      value={vehicle.registration}
                      onChange={(e) => setVehicle({ ...vehicle, registration: e.target.value })}
                      className="mt-2 w-full rounded-xl border border-[#E2E8F0] px-4 py-3 text-sm"
                      placeholder="e.g., AB12 CDE"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-[#0F172A]">Vehicle Type *</label>
                    <input
                      value={vehicle.type}
                      onChange={(e) => setVehicle({ ...vehicle, type: e.target.value })}
                      className="mt-2 w-full rounded-xl border border-[#E2E8F0] px-4 py-3 text-sm"
                      placeholder="e.g., Minibus, Sedan"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-[#0F172A]">Capacity (Number of Seats) *</label>
                  <input
                    type="number"
                    min="1"
                    max="20"
                    value={vehicle.capacity}
                    onChange={(e) => setVehicle({ ...vehicle, capacity: parseInt(e.target.value) || 1 })}
                    className="mt-2 w-full rounded-xl border border-[#E2E8F0] px-4 py-3 text-sm"
                  />
                </div>
                <div className="rounded-2xl border border-[#E2E8F0] bg-[#F8FAFC] p-4 text-sm text-[#475569]">
                  This vehicle will be automatically assigned to <strong>{driver.name || "the driver"}</strong>.
                </div>
              </div>
            </div>
          )}

          {currentStep === 7 && (
            <div className="space-y-6">
              <div>
                <h2 className="font-['Manrope'] text-xl font-semibold text-[#0F172A]">Create First Trip</h2>
                <p className="mt-1 text-sm text-[#64748B]">Schedule the first transport trip for this client</p>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-[#0F172A]">Select Student for Trip</label>
                  <select
                    value={trip.studentIndex}
                    onChange={(e) => setTrip({ ...trip, studentIndex: parseInt(e.target.value) })}
                    className="mt-2 w-full rounded-xl border border-[#E2E8F0] px-4 py-3 text-sm"
                  >
                    {students.map((student, index) => (
                      <option key={index} value={index}>
                        {student.name || `Student ${index + 1}`}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-[#0F172A]">Scheduled Start Time *</label>
                  <input
                    type="datetime-local"
                    value={trip.scheduled_start_time}
                    onChange={(e) => setTrip({ ...trip, scheduled_start_time: e.target.value })}
                    className="mt-2 w-full rounded-xl border border-[#E2E8F0] px-4 py-3 text-sm"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#0F172A]">Trip Notes</label>
                  <textarea
                    value={trip.notes}
                    onChange={(e) => setTrip({ ...trip, notes: e.target.value })}
                    className="mt-2 w-full rounded-xl border border-[#E2E8F0] px-4 py-3 text-sm"
                    rows="3"
                    placeholder="Any special instructions"
                  />
                </div>
                <div className="rounded-2xl border border-[#E2E8F0] bg-[#F8FAFC] p-4 text-sm text-[#475569]">
                  <p className="font-semibold text-[#0F172A]">Trip Summary:</p>
                  <ul className="mt-2 space-y-1">
                    <li>• Student: {students[trip.studentIndex]?.name || "Not selected"}</li>
                    <li>• Pickup: {students[trip.studentIndex]?.pickup_location || "Not set"}</li>
                    <li>• Dropoff: {students[trip.studentIndex]?.dropoff_location || "Not set"}</li>
                    <li>• Driver: {driver.name || "Not set"}</li>
                    <li>• Vehicle: {vehicle.registration || "Not set"}</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Navigation buttons */}
          <div className="mt-8 flex items-center justify-between border-t border-[#E2E8F0] pt-6">
            <button
              onClick={handleBack}
              disabled={currentStep === 1}
              className="inline-flex items-center gap-2 rounded-xl border border-[#E2E8F0] px-5 py-3 text-sm font-semibold text-[#475569] hover:bg-[#F8FAFC] disabled:cursor-not-allowed disabled:opacity-40"
            >
              <ArrowLeft size={16} />
              Back
            </button>
            <div className="text-sm text-[#64748B]">
              Step {currentStep} of {STEPS.length}
            </div>
            <button
              onClick={handleNext}
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-xl bg-[#163D6B] px-5 py-3 text-sm font-semibold text-white hover:bg-[#0F2A4D] disabled:cursor-not-allowed disabled:opacity-40"
            >
              {loading ? "Processing..." : currentStep === 7 ? "Complete Setup" : "Next"}
              {!loading && <ArrowRight size={16} />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
